import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, Edit, Trash2, FileText, Search, ChevronRight } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

function PlanoContasList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/plano-contas`);
      setItems(response.data);
    } catch (error) {
      toast.error("Erro ao carregar plano de contas");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/plano-contas/${id}`);
      toast.success("Conta excluída");
      fetchItems();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.codigo.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filterTipo === "all" || item.tipo === filterTipo;
    return matchesSearch && matchesTipo;
  });

  const getTipoBadge = (tipo) => {
    const tipos = {
      ativo: { label: "Ativo", class: "bg-blue-100 text-blue-700" },
      passivo: { label: "Passivo", class: "bg-purple-100 text-purple-700" },
      receita: { label: "Receita", class: "bg-green-100 text-green-700" },
      despesa: { label: "Despesa", class: "bg-red-100 text-red-700" },
      resultado: { label: "Resultado", class: "bg-amber-100 text-amber-700" }
    };
    return tipos[tipo] || tipos.despesa;
  };

  const getNaturezaBadge = (natureza) => {
    return natureza === "devedora" 
      ? { label: "D", class: "bg-slate-100 text-slate-700" }
      : { label: "C", class: "bg-slate-100 text-slate-700" };
  };

  // Group by nivel for hierarchical display
  const groupedItems = filteredItems.reduce((acc, item) => {
    const prefix = item.codigo.split('.')[0];
    if (!acc[prefix]) acc[prefix] = [];
    acc[prefix].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plano-contas-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Plano de Contas</h1>
        <Link to="/financeiro/plano-contas/new">
          <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Conta
          </Button>
        </Link>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por código ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <NativeSelect
              value={filterTipo}
              onValueChange={setFilterTipo}
              placeholder="Tipo"
              options={[
                { value: "all", label: "Todos os tipos" },
                { value: "ativo", label: "Ativo" },
                { value: "passivo", label: "Passivo" },
                { value: "receita", label: "Receita" },
                { value: "despesa", label: "Despesa" },
                { value: "resultado", label: "Resultado" }
              ]}
              className="w-[180px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[150px]">Código</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead className="w-[100px]">Tipo</TableHead>
                <TableHead className="w-[60px]">Nat.</TableHead>
                <TableHead className="w-[80px]">Lançar</TableHead>
                <TableHead className="w-[100px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id} className={item.nivel === 1 ? "bg-slate-50 font-semibold" : ""}>
                  <TableCell className="font-mono">
                    <span style={{ paddingLeft: `${(item.nivel - 1) * 16}px` }}>
                      {item.nivel > 1 && <ChevronRight className="h-3 w-3 inline mr-1 text-slate-400" />}
                      {item.codigo}
                    </span>
                  </TableCell>
                  <TableCell>{item.nome}</TableCell>
                  <TableCell>
                    <Badge className={getTipoBadge(item.tipo).class}>
                      {getTipoBadge(item.tipo).label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getNaturezaBadge(item.natureza).class}>
                      {getNaturezaBadge(item.natureza).label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {item.aceita_lancamento ? (
                      <Badge className="bg-green-100 text-green-700">Sim</Badge>
                    ) : (
                      <Badge variant="outline">Não</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Link to={`/financeiro/plano-contas/${item.id}/edit`}>
                        <Button variant="ghost" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir conta?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir "{item.nome}"?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(item.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    Nenhuma conta encontrada
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function PlanoContasForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    codigo: "",
    nome: "",
    tipo: "despesa",
    natureza: "devedora",
    nivel: 1,
    parent_id: null,
    aceita_lancamento: true,
    ativo: true,
    descricao: ""
  });

  const isEdit = !!id;

  useEffect(() => {
    if (isEdit) {
      fetchItem();
    }
  }, [id]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/plano-contas/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
      navigate("/financeiro/plano-contas");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/plano-contas/${id}`, formData);
        toast.success("Conta atualizada!");
      } else {
        await axios.post(`${API}/plano-contas`, formData);
        toast.success("Conta criada!");
      }
      navigate("/financeiro/plano-contas");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao salvar"));
    } finally {
      setLoading(false);
    }
  };

  // Auto-set natureza based on tipo
  useEffect(() => {
    const naturezaMap = {
      ativo: "devedora",
      passivo: "credora",
      receita: "credora",
      despesa: "devedora",
      resultado: "credora"
    };
    setFormData(prev => ({...prev, natureza: naturezaMap[prev.tipo] || "devedora"}));
  }, [formData.tipo]);

  return (
    <div className="max-w-2xl mx-auto" data-testid="plano-contas-form">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/financeiro/plano-contas">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
          {isEdit ? "Editar Conta" : "Nova Conta"}
        </h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">Código *</Label>
                <Input
                  id="codigo"
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                  placeholder="Ex: 1.1.01"
                  required
                  className="font-mono"
                />
                <p className="text-xs text-slate-500">Use pontos para hierarquia (1, 1.1, 1.1.01)</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="nivel">Nível *</Label>
                <NativeSelect
                  value={formData.nivel.toString()}
                  onValueChange={(value) => setFormData({...formData, nivel: parseInt(value)})}
                  options={[
                    { value: "1", label: "1 - Grupo" },
                    { value: "2", label: "2 - Subgrupo" },
                    { value: "3", label: "3 - Conta" },
                    { value: "4", label: "4 - Subconta" }
                  ]}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nome">Nome da Conta *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Nome da conta contábil"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <NativeSelect
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({...formData, tipo: value})}
                  options={[
                    { value: "ativo", label: "Ativo" },
                    { value: "passivo", label: "Passivo" },
                    { value: "receita", label: "Receita" },
                    { value: "despesa", label: "Despesa" },
                    { value: "resultado", label: "Resultado" }
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="natureza">Natureza</Label>
                <NativeSelect
                  value={formData.natureza}
                  onValueChange={(value) => setFormData({...formData, natureza: value})}
                  options={[
                    { value: "devedora", label: "Devedora (D)" },
                    { value: "credora", label: "Credora (C)" }
                  ]}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição da conta"
                rows={2}
              />
            </div>

            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="aceita_lancamento" className="font-medium">Aceita Lançamentos</Label>
                  <p className="text-sm text-slate-500">Permite lançamentos nesta conta</p>
                </div>
                <Switch
                  id="aceita_lancamento"
                  checked={formData.aceita_lancamento}
                  onCheckedChange={(checked) => setFormData({...formData, aceita_lancamento: checked})}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="ativo" className="font-medium">Conta Ativa</Label>
                  <p className="text-sm text-slate-500">Conta disponível para uso</p>
                </div>
                <Switch
                  id="ativo"
                  checked={formData.ativo}
                  onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
              disabled={loading}
            >
              {loading ? "Salvando..." : (isEdit ? "Atualizar" : "Criar Conta")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PlanoContasPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<PlanoContasList />} />
        <Route path="new" element={<PlanoContasForm />} />
        <Route path=":id/edit" element={<PlanoContasForm />} />
      </Routes>
    </MainLayout>
  );
}
